import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getImageUrl, createFallbackImage } from '../utils/imageUtils';
import './PromoBanner.css';

const PromoBanner = ({ products }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Número de produtos a serem exibidos no banner
  const [displayCount, setDisplayCount] = useState(3);
  
  // Referência para o container do banner
  const bannerRef = useRef(null);
  
  // Atualizar o número de produtos exibidos com base no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setDisplayCount(1);
      } else if (window.innerWidth < 1024) {
        setDisplayCount(2);
      } else {
        setDisplayCount(3);
      }
    };
    
    // Definir o valor inicial
    handleResize();
    
    // Adicionar event listener para redimensionamento
    window.addEventListener('resize', handleResize);
    
    // Limpar event listener
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Embaralhar produtos quando o componente for montado
  useEffect(() => {
    if (products && products.length > 0) {
      // Filtrar apenas produtos com desconto (preço promocional menor que o preço regular)
      const discountedProducts = products.filter(
        product => product.salePrice && product.salePrice < product.price
      );
      
      // Embaralhar os produtos com desconto
      const shuffled = [...discountedProducts].sort(() => Math.random() - 0.5);
      
      // Definir os produtos visíveis iniciais
      updateVisibleProducts(shuffled, 0);
    }
  }, [products]);
  
  // Atualizar produtos visíveis com base no índice atual
  const updateVisibleProducts = (productList, index) => {
    if (!productList || productList.length === 0) return;
    
    const productsCount = productList.length;
    const visibleItems = [];
    
    for (let i = 0; i < displayCount; i++) {
      const productIndex = (index + i) % productsCount;
      visibleItems.push(productList[productIndex]);
    }
    
    // Inicia a transição
    setIsTransitioning(true);
    
    // Após a animação de saída (500ms), atualiza os produtos
    setTimeout(() => {
      setVisibleProducts(visibleItems);
      
      // Remove a classe de transição para iniciar a animação de entrada
      requestAnimationFrame(() => {
        setIsTransitioning(false);
      });
    }, 500);
  };
  
  // Avançar para o próximo conjunto de produtos
  const nextSlide = () => {
    const discountedProducts = products.filter(
      product => product.salePrice && product.salePrice < product.price
    );
    
    if (discountedProducts.length <= displayCount) return;
    
    const newIndex = (currentIndex + 1) % discountedProducts.length;
    setCurrentIndex(newIndex);
    updateVisibleProducts(discountedProducts, newIndex);
  };
  
  // Voltar para o conjunto anterior de produtos
  const prevSlide = () => {
    const discountedProducts = products.filter(
      product => product.salePrice && product.salePrice < product.price
    );
    
    if (discountedProducts.length <= displayCount) return;
    
    const newIndex = (currentIndex - 1 + discountedProducts.length) % discountedProducts.length;
    setCurrentIndex(newIndex);
    updateVisibleProducts(discountedProducts, newIndex);
  };
  
  // Rotação automática a cada 5 segundos
  useEffect(() => {
    const discountedProducts = products?.filter(
      product => product.salePrice && product.salePrice < product.price
    ) || [];
    
    if (discountedProducts.length <= displayCount || isPaused) return;
    
    const interval = setInterval(() => {
      if (!isTransitioning) {
        const newIndex = (currentIndex + 1) % discountedProducts.length;
        setCurrentIndex(newIndex);
        updateVisibleProducts(discountedProducts, newIndex);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [currentIndex, products, displayCount, isPaused, isTransitioning]);
  
  // Formatar preço em reais
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };
  
  // Calcular porcentagem de desconto
  const calculateDiscount = (originalPrice, salePrice) => {
    return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
  };
  
  if (!visibleProducts || visibleProducts.length === 0) {
    return null;
  }
  
  // Calcular número total de slides
  const getTotalSlides = () => {
    const discountedProducts = products?.filter(
      product => product.salePrice && product.salePrice < product.price
    ) || [];
    return Math.ceil(discountedProducts.length / displayCount);
  };

  // Gerar indicadores de slide
  const renderIndicators = () => {
    const totalSlides = getTotalSlides();
    return (
      <div className="promo-banner-indicators">
        {Array.from({ length: totalSlides }, (_, index) => (
          <button
            key={index}
            className={`indicator ${index === Math.floor(currentIndex / displayCount) ? 'active' : ''}`}
            onClick={() => {
              const newIndex = index * displayCount;
              setCurrentIndex(newIndex);
              updateVisibleProducts(
                products.filter(p => p.salePrice && p.salePrice < p.price),
                newIndex
              );
            }}
            aria-label={`Ir para slide ${index + 1}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div 
      className="promo-banner"
      ref={bannerRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="promo-banner-header">
        <h2>✨ MEGA OFERTAS ✨</h2>
        <p>Produtos com super descontos para você!</p>
      </div>
      
      <div className="promo-banner-container">
        <button 
          className="promo-banner-nav prev" 
          onClick={prevSlide}
          aria-label="Produtos anteriores"
        >
          <i className="fas fa-chevron-left"></i>
        </button>
        
        <div className={`promo-banner-items ${displayCount === 1 ? 'single-item' : ''}`}>
          {visibleProducts.map((product, index) => (
            <Link 
              to={`/produto/${product.code}`} 
              className="promo-banner-item" 
              key={`${product.code}-${index}`}
            >
              <div className="promo-banner-image">
                <img 
                  src={getImageUrl(product.imagePath || `/imagens/${product.code}.png`)}
                  alt={product.name}
                  onError={(e) => {
                    e.target.onerror = null;
                    // Log aprimorado para diagnóstico
                    console.error(`Falha ao carregar imagem. URL: ${e.target.src}`, { product });
                    e.target.src = createFallbackImage(product.name, 200, 200);
                    e.target.classList.add('fallback-image');
                  }}
                  loading="lazy"
                  crossOrigin="anonymous"
                />
                <div className="discount-badge">
                  {calculateDiscount(product.price, product.salePrice)}% OFF
                </div>
              </div>
              <div className="promo-banner-info">
                <h3>{product.name}</h3>
                <div className="promo-banner-price">
                  <span className="original-price">{formatPrice(product.price)}</span>
                  <span className="sale-price">{formatPrice(product.salePrice)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        
        <button 
          className="promo-banner-nav next" 
          onClick={nextSlide}
          aria-label="Próximos produtos"
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
      {renderIndicators()}
    </div>
  );
};

export default PromoBanner;
